import {
  AssemblyFilter,
  AssemblyRegistrationStatus,
  AssemblySortProperty,
} from '@/assembly/api/get-assemblies';
import { Assembly } from '@/assembly/api/types';
import { AssemblyTable } from '@/assembly/components/assembly-table';
import { AssemblyTableSkeleton } from '@/assembly/components/assembly-table-skeleton';
import { CreateAssemblyDialog } from '@/assembly/components/create-assembly-dialog';
import { RegisterAssemblyDialog } from '@/assembly/components/register-assembly-dialog';
import { EducationalTooltip } from '@/components/educational-tooltip';
import {
  FilterOption,
  FilterToggleGroup,
} from '@/components/filter-toggle-group';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { TableControlContainer } from '@/components/table-control-container';
import {
  AddRounded,
  ClearRounded,
  InfoOutlined,
} from '@/constants/hardfin-standard-icons';
import { SEARCH_TERM_MAXIMUM_CHARACTERS } from '@/constants/search-term';
import { SortDirection, invert } from '@/constants/sort';
import { useDisclosure } from '@/hooks/use-disclosure';
import { ItemFilterMenu } from '@/items/components/item-filter-menu';
import { LocationFilterMenu } from '@/location/components/location-filter-menu';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { debounce, omit } from 'lodash-es';
import {
  ChangeEventHandler,
  Suspense,
  useEffect,
  useMemo,
  useRef,
} from 'react';

/**
 * Navigation options for the assembly listing.
 */
interface NavigateOptions {
  to?: string;
  params?: Record<string, string>;
  search?: Partial<AssemblyFilter> & Record<string, unknown>;
}

interface Props {
  searchParameters: AssemblyFilter;
  statusOptions: FilterOption<AssemblyRegistrationStatus>[];
  navigateFn: (options: NavigateOptions) => void;
}

export function AssemblyListing({
  searchParameters,
  statusOptions,
  navigateFn,
}: Props) {
  const searchContainer = useRef<HTMLInputElement>(null);
  const activeAssembly = useRef<Assembly | null>(null);
  const createAssemblyDialog = useDisclosure();
  const registerAssemblyDialog = useDisclosure();
  const infoTooltip = useDisclosure();
  const infoButtonRef = useRef<HTMLButtonElement>(null);

  const currentStatus: AssemblyRegistrationStatus =
    searchParameters.registrationStatus ?? 'unregistered';

  const updateQuery = useMemo(() => {
    return debounce((query: string) => {
      if (query.length === 0) {
        navigateFn({
          search: { ...omit(searchParameters, 'page', 'query') },
        });
        return;
      }
      navigateFn({
        search: { ...omit(searchParameters, 'page'), query },
      });
    }, 300);
  }, [navigateFn, searchParameters]);

  // Cancel on unmount
  useEffect(() => {
    return () => {
      updateQuery.cancel();
    };
  }, [updateQuery]);

  const sort = {
    property: searchParameters.sort ?? ('buildDate' as AssemblySortProperty),
    direction: searchParameters.direction ?? ('asc' as SortDirection),
  };

  const handleSort = (property: AssemblySortProperty) => {
    if (property === sort.property) {
      navigateFn({
        search: {
          ...omit(searchParameters, 'registrationStatus'),
          direction: invert(sort.direction),
        },
      });
    } else {
      navigateFn({
        search: {
          ...omit(searchParameters, 'registrationStatus'),
          sort: property,
          direction: 'asc',
        },
      });
    }
  };

  const handleStatusChange = (newStatus: AssemblyRegistrationStatus) => {
    navigateFn({
      to: '/assemblies/$registrationStatus',
      params: { registrationStatus: newStatus },
      search: omit(searchParameters, 'page', 'registrationStatus'),
    });
  };

  const handleChangeProducts = (productIds: string[]) => {
    const searchParams = omit(searchParameters, 'page');
    if (productIds.length === 0) {
      delete searchParams.productIds;
    } else {
      searchParams.productIds = productIds;
    }
    navigateFn({ search: searchParams });
  };

  const handleChangeLocations = (locationIds: string[]) => {
    const searchParams = omit(searchParameters, 'page');
    if (locationIds.length === 0) {
      delete searchParams.locationIds;
    } else {
      searchParams.locationIds = locationIds;
    }
    navigateFn({ search: searchParams });
  };

  const handlePageChange = (page: number) => {
    navigateFn({
      search: { ...searchParameters, page },
    });
  };

  const handleChangeQuery: ChangeEventHandler<HTMLInputElement> = (event) => {
    updateQuery(event.target.value);
  };

  const handleCreateAssembly = () => {
    createAssemblyDialog.open();
  };

  const handleRegisterAssembly = (assembly: Assembly) => {
    activeAssembly.current = assembly;
    registerAssemblyDialog.open();
  };

  return (
    <Stack>
      <CreateAssemblyDialog
        isOpen={createAssemblyDialog.isOpen}
        onClose={createAssemblyDialog.close}
      />
      <RegisterAssemblyDialog
        isOpen={registerAssemblyDialog.isOpen}
        onClose={registerAssemblyDialog.close}
        activeAssembly={activeAssembly.current ?? null}
      />

      {/* Action bar */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" alignItems="center" spacing={1}>
          <FilterToggleGroup
            value={currentStatus}
            options={statusOptions}
            onChange={handleStatusChange}
            ariaLabel="Assembly registration status filter"
          />
          <IconButton
            ref={infoButtonRef}
            size="small"
            onClick={infoTooltip.open}
            aria-label="Learn about assemblies"
          >
            <InfoOutlined fontSize="small" />
          </IconButton>
        </Stack>
        <Button
          variant="contained"
          startIcon={<AddRounded fontSize="small" />}
          onClick={handleCreateAssembly}
        >
          Create assembly
        </Button>
      </Stack>

      {/* Educational tooltip explaining assemblies and registration status */}
      <EducationalTooltip
        title="Assemblies"
        isOpen={infoTooltip.isOpen}
        onClose={infoTooltip.close}
        anchorElement={infoButtonRef.current}
        placement="bottom-start"
      >
        <Stack spacing={2}>
          <Box>
            <Typography variant="body2">
              <strong>Assemblies</strong> are collections of parts that are
              built together. Assemblies are usually built based on a bill of
              materials (BOM) defined for a product. Assembly is the final step
              in the parts workflow before parts become a production asset.
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Assembly registration status
            </Typography>
            <Typography variant="body2">
              <strong>Unregistered:</strong> Assembly has been built but not yet
              registered as an asset. Parts have been drawn from inventory, and
              the assembly can still be modified or disassembled.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, mb: 0 }}>
              <strong>Registered:</strong> Assembly has been registered as an
              asset. The assembly links to the registered asset and can no
              longer be updated. Any modifications must be made to the asset
              itself.
            </Typography>
          </Box>
          <Typography variant="body2">
            <Link
              href="https://guide.hardfin.com/source-and-build/assemblies"
              target="_blank"
            >
              Read more about assemblies
            </Link>
          </Typography>
        </Stack>
      </EducationalTooltip>

      {/* Table controls */}
      <TableControlContainer>
        <Stack
          useFlexGap
          spacing={1}
          flexWrap="wrap"
          direction="row"
          marginRight="auto"
        >
          <ItemFilterMenu
            label="Product"
            value={searchParameters.productIds ?? []}
            type="DEVICE"
            includeArchived={true}
            onChange={handleChangeProducts}
          />
          <LocationFilterMenu
            value={searchParameters.locationIds ?? []}
            onChange={handleChangeLocations}
          />
        </Stack>
        <TextField
          inputRef={searchContainer}
          size="small"
          label="Search assemblies"
          defaultValue={searchParameters.query}
          inputProps={{ maxLength: SEARCH_TERM_MAXIMUM_CHARACTERS }}
          onChange={handleChangeQuery}
          sx={{ width: { xs: '100%', sm: '20rem' } }}
          InputProps={{
            endAdornment: searchParameters.query && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    if (searchContainer.current) {
                      searchContainer.current.value = '';
                    }
                    updateQuery('');
                  }}
                >
                  <ClearRounded fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </TableControlContainer>

      <SupportErrorBoundary message="Something went wrong loading the assemblies">
        <Suspense fallback={<AssemblyTableSkeleton />}>
          <AssemblyTable
            sort={sort}
            filter={searchParameters}
            onPageChange={handlePageChange}
            onSort={handleSort}
            handleRegisterAssembly={handleRegisterAssembly}
          />
        </Suspense>
      </SupportErrorBoundary>
    </Stack>
  );
}
